import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { CircularProgress, LinearScore } from "../components/Progress";
import { ArrowLeft, Sparkles, Heart, X, MessageCircle, Copy, Check, MoreVertical, Flag, ShieldOff } from "lucide-react";

export default function ProfileDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [acting, setActing] = useState(false);
  const [matched, setMatched] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportCat, setReportCat] = useState("harassment");
  const [reportDetail, setReportDetail] = useState("");
  const [reportSent, setReportSent] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/compatibility/${id}`);
        setData(data);
      } catch (e) { setError("Nepodařilo se načíst profil."); }
    })();
  }, [id]);

  const act = async (action) => {
    setActing(true);
    try {
      const { data: r } = await api.post("/like", { target_user_id: id, action });
      if (r.matched) setMatched(r);
      else nav("/discover");
    } finally { setActing(false); }
  };

  const doBlock = async () => {
    if (!window.confirm("Opravdu chceš tuto osobu zablokovat? Už ji neuvidíš.")) return;
    try {
      await api.post("/block", { target_user_id: id });
      setMenuOpen(false);
      nav("/discover");
    } catch (e) {
      alert(e.response?.data?.detail || "Nepodařilo se zablokovat.");
    }
  };

  const submitReport = async () => {
    if (reportDetail.trim().length < 3) { alert("Napiš prosím krátký důvod (min. 3 znaky)."); return; }
    try {
      await api.post("/report", { target_user_id: id, category: reportCat, detail: reportDetail });
      setReportSent(true);
      setTimeout(() => { setReportOpen(false); nav("/discover"); }, 1800);
    } catch (e) {
      alert(e.response?.data?.detail || "Nepodařilo se odeslat.");
    }
  };

  if (error) return <div className="app-shell bg-aurora min-h-screen p-10 text-center text-rose-600">{error}</div>;
  if (!data) return <LoadingDetail />;

  const { target, compat } = data;
  const cats = Object.entries(compat.categories || {});

  return (
    <div className="app-shell bg-aurora min-h-screen pb-32" data-testid="profile-detail-page">
      {/* Hero photo */}
      <div className="relative h-[480px]">
        {target.photo_url ? (
          <img src={target.photo_url} alt={target.name} className="w-full h-full object-cover" />
        ) : <div className="w-full h-full bg-gradient-to-br from-[#DEE7E2] to-[#DEE7E2] flex items-center justify-center text-7xl">💖</div>}
        <button onClick={() => nav(-1)} className="absolute top-6 left-6 w-10 h-10 rounded-full bg-white/80 backdrop-blur flex items-center justify-center shadow-md" data-testid="detail-back">
          <ArrowLeft size={18} strokeWidth={1.7} />
        </button>
        {/* Menu button - block/report */}
        <button onClick={() => setMenuOpen(true)} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/80 backdrop-blur flex items-center justify-center shadow-md" data-testid="detail-menu">
          <MoreVertical size={18} strokeWidth={1.7} />
        </button>
        <div className="absolute top-20 right-6 backdrop-blur-md bg-white/85 border border-white/60 rounded-full px-3 py-1.5 flex items-center gap-1.5 shadow-md">
          <Sparkles size={14} className="text-[#082A24]" />
          <span className="font-display font-semibold text-[#C9A66B]">{compat.overall_score}%</span>
        </div>
        <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent text-white">
          <h1 className="font-display text-4xl">{target.name}, {target.age}</h1>
          {target.bio && <p className="text-sm opacity-90 mt-1.5 leading-relaxed">{target.bio}</p>}
        </div>
      </div>

      <div className="px-6 -mt-8 relative z-10 space-y-5">
        {/* Overall score card */}
        <div className="glass-card p-6 flex items-center gap-5">
          <CircularProgress value={compat.overall_score} size={110} stroke={9} label="kompatibilita" />
          <div>
            <p className="text-xs uppercase tracking-widest text-[#082A24] font-medium">AI vyhodnotila</p>
            <h2 className="font-display text-2xl text-[#332D38] leading-tight">Celková kompatibilita</h2>
            <p className="text-xs text-[#8A7E91] mt-1">Podle 7 oblastí</p>
          </div>
        </div>

        {/* AI explanation */}
        <div className="rounded-3xl p-6 bg-gradient-to-br from-[#F0E8D6] via-[#EDE5D2] to-[#F5EFE0] border border-[#DEE7E2] shadow-[0_8px_30px_rgba(217,70,239,0.1)]">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#082A24] to-[#0F3D34] flex items-center justify-center"><Sparkles size={14} className="text-white" /></div>
            <span className="font-display text-lg text-[#332D38]">AI vysvětlení</span>
          </div>
          <p className="text-[#332D38] leading-relaxed" data-testid="ai-explanation">{compat.explanation}</p>
        </div>

        {/* Categories */}
        <div className="glass-card p-6 space-y-4">
          <h3 className="font-display text-xl text-[#332D38] mb-2">Rozpis podle oblastí</h3>
          {cats.map(([key, c]) => (
            <LinearScore key={key} value={c.score} label={c.label} />
          ))}
        </div>

        {/* Strengths & watch-outs */}
        {compat.strengths?.length > 0 && (
          <div className="glass-card p-6">
            <h3 className="font-display text-xl text-[#332D38] mb-3">✨ Silné stránky</h3>
            <ul className="space-y-2">
              {compat.strengths.map((s, i) => <li key={`str-${i}-${s.slice(0,20)}`} className="flex items-start gap-2 text-sm text-[#332D38]"><span className="text-emerald-500 mt-0.5">●</span>{s}</li>)}
            </ul>
          </div>
        )}
        {compat.watch_outs?.length > 0 && (
          <div className="glass-card p-6">
            <h3 className="font-display text-xl text-[#332D38] mb-3">⚠️ Co probrat</h3>
            <ul className="space-y-2">
              {compat.watch_outs.map((s, i) => <li key={`wo-${i}-${s.slice(0,20)}`} className="flex items-start gap-2 text-sm text-[#332D38]"><span className="text-[#B8915A] mt-0.5">●</span>{s}</li>)}
            </ul>
          </div>
        )}

        {/* Icebreakers */}
        {compat.icebreakers?.length > 0 && (
          <div className="rounded-3xl p-6 bg-gradient-to-br from-[#F5EFE0] to-[#EDE5D2] border border-[#DEE7E2]">
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle size={18} className="text-[#082A24]" />
              <span className="font-display text-lg text-[#332D38]">AI ledoborec — návrhy první zprávy</span>
            </div>
            <div className="space-y-3">
              {compat.icebreakers.map((m, i) => <Icebreaker key={`ib-${i}-${m.slice(0,20)}`} text={m} idx={i} />)}
            </div>
          </div>
        )}
      </div>

      {/* Action buttons fixed bottom */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md p-5 bg-gradient-to-t from-white via-white/95 to-transparent">
        <div className="flex gap-3">
          <button onClick={() => act("skip")} disabled={acting} className="flex-1 py-3.5 rounded-full bg-white border border-slate-200 text-slate-500 flex items-center justify-center gap-1.5 shadow-md" data-testid="detail-skip">
            <X size={18} /> Přeskočit
          </button>
          <button onClick={() => act("like")} disabled={acting} className="flex-1 py-3.5 rounded-full bg-gradient-to-r from-[#082A24] to-[#0F3D34] text-white flex items-center justify-center gap-1.5 shadow-lg" data-testid="detail-like">
            <Heart size={18} fill="white" strokeWidth={0} /> Líbí se mi
          </button>
        </div>
      </div>

      {matched && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
            <div className="heart-pulse text-6xl mb-3">💖</div>
            <h2 className="font-display text-3xl text-[#332D38] mb-2">Je to match!</h2>
            <p className="text-[#54635F] mb-6">Ty a <strong>{target.name}</strong> jste se líbili navzájem.</p>
            <Link to={`/chat/${matched.match_id}`}><button className="btn-primary mb-3">Otevřít chat ✨</button></Link>
            <button onClick={() => nav("/discover")} className="text-sm text-[#8A7E91]">Pokračovat v objevování</button>
          </div>
        </div>
      )}

      {/* Menu: block / report */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setMenuOpen(false)} data-testid="detail-menu-modal">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl p-4 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-1 bg-slate-200 rounded-full mx-auto mb-4" />
            <button onClick={() => { setMenuOpen(false); setReportOpen(true); }} className="w-full py-4 flex items-center gap-3 rounded-2xl active:bg-slate-50" data-testid="menu-report">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center"><Flag size={18} className="text-orange-600" /></div>
              <div className="text-left flex-1"><p className="font-medium text-[#332D38]">Nahlásit</p><p className="text-xs text-[#8A7E91]">Nevhodné chování, spam, falešný profil</p></div>
            </button>
            <button onClick={doBlock} className="w-full py-4 flex items-center gap-3 rounded-2xl active:bg-slate-50" data-testid="menu-block">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center"><ShieldOff size={18} className="text-rose-600" /></div>
              <div className="text-left flex-1"><p className="font-medium text-[#332D38]">Zablokovat</p><p className="text-xs text-[#8A7E91]">Nikdy víc neuvidíš tento profil</p></div>
            </button>
            <button onClick={() => setMenuOpen(false)} className="w-full py-3 mt-2 rounded-2xl text-[#8A7E91]" data-testid="menu-cancel">Zrušit</button>
          </div>
        </div>
      )}

      {/* Report modal */}
      {reportOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="report-modal">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl p-6 w-full max-w-md">
            {reportSent ? (
              <div className="text-center py-6">
                <div className="text-5xl mb-3">✅</div>
                <p className="font-display text-xl text-[#332D38]">Díky za nahlášení</p>
                <p className="text-sm text-[#54635F] mt-1">Vyřídíme do 24 hodin.</p>
              </div>
            ) : (
              <>
                <h3 className="font-display text-xl text-[#332D38] mb-2">Nahlásit {target.name}</h3>
                <p className="text-xs text-[#8A7E91] mb-4">Nahlášení posíláme moderátorům. Osobu automaticky zablokujeme.</p>
                <label className="label-soft">Důvod</label>
                <select value={reportCat} onChange={(e) => setReportCat(e.target.value)} className="input-field mb-3" data-testid="report-category">
                  <option value="fake_profile">Falešný profil</option>
                  <option value="harassment">Obtěžování / vulgární chování</option>
                  <option value="inappropriate_content">Nevhodné fotky / obsah</option>
                  <option value="spam">Spam / reklama</option>
                  <option value="underage">Podezření na nezletilost</option>
                  <option value="scam">Podvod / vylákávání peněz</option>
                  <option value="other">Jiný důvod</option>
                </select>
                <label className="label-soft">Popiš situaci</label>
                <textarea rows={3} value={reportDetail} onChange={(e) => setReportDetail(e.target.value)} className="input-field mb-4" placeholder="Co se stalo?" data-testid="report-detail" />
                <div className="flex gap-3">
                  <button onClick={() => setReportOpen(false)} className="flex-1 py-3 rounded-full border border-slate-200 text-[#54635F]" data-testid="report-cancel">Zrušit</button>
                  <button onClick={submitReport} className="flex-1 py-3 rounded-full bg-rose-600 text-white font-medium" data-testid="report-submit">Odeslat</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Icebreaker({ text, idx }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#DEE7E2]/50 flex items-start gap-3" data-testid={`icebreaker-${idx}`}>
      <p className="text-sm text-[#332D38] flex-1 leading-relaxed">{text}</p>
      <button onClick={copy} className="text-[#082A24] shrink-0 p-1.5 hover:bg-[#FFE4CC] rounded-full" data-testid={`copy-${idx}`}>
        {copied ? <Check size={16} /> : <Copy size={16} />}
      </button>
    </div>
  );
}

function LoadingDetail() {
  return (
    <div className="app-shell bg-aurora min-h-screen p-6" data-testid="profile-detail-loading">
      <div className="h-[480px] rounded-3xl shimmer mb-6" />
      <div className="text-center">
        <p className="font-display text-lg text-[#332D38]">✨ AI počítá kompatibilitu…</p>
        <p className="text-xs text-[#8A7E91] mt-1">Vydrž chvilku, prohledáváme vaše odpovědi.</p>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-6">
        <div className="h-24 rounded-2xl shimmer" />
        <div className="h-24 rounded-2xl shimmer" />
      </div>
    </div>
  );
}
